import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, Navigate, useSearchParams } from 'react-router-dom'
import { Icon } from '../components/ui/Icon'
import { actionSourceLabel, stateLabel } from '../lib/display-labels'
import { useTenant } from '../tenancy/TenantProvider'
import { ActionManagementControls } from './ActionManagementControls'
import { actionSummaryCounts, groupActionsByPresentationState } from './action-presentation-state'
import {
  getActionsReadModel,
  type ActionsCursor,
  type ActionsReadModelItem,
} from './governance-api'

type ActionsView = 'my' | 'organisation'

const actionTypeOptions = [
  ['confirmation_requirement', 'Specialist confirmation'],
  ['information_request', 'Information request'],
  ['approval_requirement', 'Approval requirement'],
  ['ai_use_business_owner', 'AI Use business owner'],
  ['ai_use_technical_owner', 'AI Use technical owner'],
  ['organisation_ai_solution_technical_owner', 'Solution technical owner'],
  ['control_assignment_owner', 'Control assignment owner'],
  ['action_owner', 'Action owner'],
  ['periodic_review_assignee', 'Periodic Review assignee'],
  ['approval_condition_owner', 'Approval Condition owner'],
  ['risk_owner', 'Risk owner'],
  ['risk_acceptance_assignee', 'Risk Acceptance assignee'],
  ['initiative_owner', 'Initiative owner'],
  ['discovery_assignment', 'Discovery assignment'],
  ['discovery_submission_contributor', 'Discovery submission contributor'],
  ['candidate_clarification_recipient', 'Candidate Clarification recipient'],
] as const

const stateOptions = [
  ['open', 'Open'],
  ['waiting_on_others', 'Waiting on others'],
  ['blocked', 'Blocked'],
  ['pending_recipient', 'Pending recipient'],
  ['unassigned', 'Unassigned'],
  ['completed', 'Completed'],
  ['draft', 'Draft'],
  ['submitted', 'Submitted'],
] as const

function dateLabel(value: string | null, fallback: string) {
  if (!value) return fallback
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

function assigneeLabel(action: ActionsReadModelItem) {
  if (action.assigneeName) return action.assigneeName
  if (action.assignmentState === 'pending_recipient') return 'Pending recipient'
  if (action.assignmentState === 'unassigned') return 'Unassigned'
  return 'No assignee recorded'
}

function hasFilters(actionType: string, status: string, due: string) {
  return Boolean(actionType || status || due)
}

function primaryActionLabel(action: ActionsReadModelItem) {
  if (action.sourceType === 'information_request' && action.state !== 'waiting_on_others') return 'Respond'
  if (action.sourceType === 'confirmation_requirement' || action.sourceType === 'approval_requirement') return 'Review'
  return 'Open'
}

function ActionRow({ action }: { action: ActionsReadModelItem }) {
  const content = <>
    <div className="actions-row-heading">
      <span className="actions-row-icon" aria-hidden="true"><Icon name="work" size={18} /></span>
      <div>
        <strong>{action.actionType || actionSourceLabel(action.sourceType)}</strong>
        <span>{action.aiUseName ?? 'No AI Use is linked to this source.'}</span>
      </div>
      <span className={`actions-state actions-state-${action.state || 'unknown'}`}>{stateLabel(action.state || action.status || 'unknown')}</span>
      {action.canOpenDetail && action.deepLinkPath ? <Icon name="arrow-right" size={17} aria-hidden="true" /> : null}
    </div>
    <dl className="actions-row-details">
      <div><dt>Assignee</dt><dd>{assigneeLabel(action)}</dd></div>
      <div><dt>Status</dt><dd>{stateLabel(action.status || 'not recorded')}</dd></div>
      <div><dt>Due date</dt><dd>{dateLabel(action.dueAt, 'No due date recorded')}</dd></div>
      <div><dt>Blocker</dt><dd>{action.blocker ?? 'No blocker'}</dd></div>
    </dl>
    <div className="actions-row-footer"><small className="actions-row-source">{actionSourceLabel(action.sourceType)}</small>{action.canOpenDetail && action.deepLinkPath ? <span className="button secondary compact">{primaryActionLabel(action)}</span> : null}</div>
  </>

  if (action.canOpenDetail && action.deepLinkPath) {
    return <article className="actions-row">
      <Link className="actions-row-link" to={action.deepLinkPath} aria-label={`Open ${action.actionType || actionSourceLabel(action.sourceType)}`}>
        {content}
      </Link>
      <ActionManagementControls action={action} />
    </article>
  }

  return <article className="actions-row" aria-label={`${action.actionType || actionSourceLabel(action.sourceType)} — detail is not available from Actions`}>
    {content}
    <ActionManagementControls action={action} />
  </article>
}

export function ActionsPage() {
  const { current } = useTenant()
  const [searchParams, setSearchParams] = useSearchParams()
  const [cursors, setCursors] = useState<(ActionsCursor | null)[]>([null])
  const [pageIndex, setPageIndex] = useState(0)
  const organisationId = current?.organisation.id ?? ''
  const view: ActionsView = searchParams.get('view') === 'organisation' ? 'organisation' : 'my'
  const actionType = searchParams.get('type') ?? ''
  const status = searchParams.get('status') ?? ''
  const due = searchParams.get('due') ?? ''
  const filtersKey = `${view}|${actionType}|${status}|${due}`
  const cursor = cursors[pageIndex] ?? null

  useEffect(() => {
    setCursors([null])
    setPageIndex(0)
  }, [filtersKey])

  const queryInput = useMemo(() => ({
    organisationId,
    view,
    actionType: actionType || null,
    status: status || null,
    overdue: due === 'overdue' ? true : due === 'not-overdue' ? false : null,
    cursor,
    limit: 25,
  }), [organisationId, view, actionType, status, due, cursor])

  const actionsQuery = useQuery({
    queryKey: ['r1-actions-read-model', queryInput],
    queryFn: () => getActionsReadModel(queryInput),
    enabled: Boolean(organisationId),
    retry: false,
  })

  if (!current) return <Navigate to="/workspace/create" replace />

  const updateParam = (key: 'type' | 'status' | 'due', value: string) => {
    const next = new URLSearchParams(searchParams)
    if (value) next.set(key, value)
    else next.delete(key)
    setSearchParams(next)
  }
  const clearFilters = () => setSearchParams(view === 'organisation' ? { view } : {})
  const data = actionsQuery.data
  const summaryCounts = actionSummaryCounts(data)
  const groupedActions = groupActionsByPresentationState(data?.items ?? [])
  const filtersApplied = hasFilters(actionType, status, due)
  const title = view === 'my' ? 'My actions' : 'All organisation actions'
  const emptyTitle = filtersApplied
    ? 'No actions match these filters'
    : view === 'my' ? 'There are no actions waiting for you' : 'There are no organisation actions at the moment'

  const tabTo = (target: ActionsView) => {
    const next = new URLSearchParams(searchParams)
    if (target === 'organisation') next.set('view', target)
    else next.delete('view')
    return `/actions${next.size ? `?${next.toString()}` : ''}`
  }

  const moveToNextPage = () => {
    if (!data?.nextCursor) return
    setCursors((currentCursors) => [...currentCursors.slice(0, pageIndex + 1), data.nextCursor])
    setPageIndex((currentPage) => currentPage + 1)
  }

  return <main className="page-stack actions-page">
    <header className="page-header actions-page-header">
      <div>
        <p className="eyebrow">Actions</p>
        <h1>{title}</h1>
        <p>{view === 'my'
          ? 'Your open actions.'
          : 'Permitted organisation-wide action metadata. Actions does not grant detail, edit or decision authority.'}</p>
      </div>
      {view === 'my' ? <div className="actions-counts" aria-live="polite">
        {actionsQuery.isLoading ? <span className="actions-count">Loading actions…</span> : <>
          <span className="actions-count"><strong>{summaryCounts.requiresAction}</strong> Requires action</span>
          <span className="actions-count"><strong>{summaryCounts.waiting}</strong> Waiting</span>
          {summaryCounts.information ? <span className="actions-count"><strong>{summaryCounts.information}</strong> For information</span> : null}
        </>}
      </div> : <span className="actions-count" aria-live="polite">{actionsQuery.isLoading ? 'Loading actions…' : `${data?.totalCount ?? 0} total`}</span>}
    </header>

    <nav className="actions-tabs" aria-label="Actions view">
      <Link className={view === 'my' ? 'active' : ''} to={tabTo('my')} aria-current={view === 'my' ? 'page' : undefined}>My actions</Link>
      <Link className={view === 'organisation' ? 'active' : ''} to={tabTo('organisation')} aria-current={view === 'organisation' ? 'page' : undefined}>All organisation actions</Link>
    </nav>

    <section className="panel actions-filter-panel" aria-label="Filter actions">
      <div className="actions-filter-grid">
        <label>Action type
          <select aria-label="Action type" value={actionType} onChange={(event) => updateParam('type', event.target.value)}>
            <option value="">All action types</option>
            {actionTypeOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label>State or status
          <select aria-label="State or status" value={status} onChange={(event) => updateParam('status', event.target.value)}>
            <option value="">All states and statuses</option>
            {stateOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label>Due date
          <select aria-label="Due date" value={due} onChange={(event) => updateParam('due', event.target.value)}>
            <option value="">All due dates</option>
            <option value="overdue">Overdue only</option>
            <option value="not-overdue">Not overdue or no due date</option>
          </select>
        </label>
      </div>
      {filtersApplied ? <button className="button text compact" type="button" onClick={clearFilters}>Clear filters</button> : null}
    </section>

    <section className="actions-results" aria-live="polite" aria-busy={actionsQuery.isLoading}>
      {actionsQuery.isLoading ? <div className="panel actions-message"><h2>Loading actions</h2><p>Checking the current governed sources for this view.</p></div> : null}
      {actionsQuery.isError ? <div className="panel actions-message error" role="alert"><h2>Actions could not be loaded</h2><p>Your recorded work has not been changed. Try again shortly or use the relevant source area.</p></div> : null}
      {!actionsQuery.isLoading && !actionsQuery.isError && data?.items.length === 0 ? <div className="panel actions-message"><h2>{emptyTitle}</h2><p>{filtersApplied ? 'Change or clear the filters to see other permitted action metadata.' : 'New governed work will appear here when the source record makes it available.'}</p></div> : null}
      {!actionsQuery.isLoading && !actionsQuery.isError && data?.items.length ? (view === 'my' ? <div className="actions-groups">
        {groupedActions.requires_action.length ? <section className="actions-state-group" aria-labelledby="actions-requires-heading"><h2 id="actions-requires-heading">Requires action</h2><p>Items you can act on now.</p><div className="actions-list">{groupedActions.requires_action.map((action) => <ActionRow key={`${action.sourceType}-${action.sourceId}`} action={action} />)}</div></section> : null}
        {groupedActions.waiting.length ? <section className="actions-state-group" aria-labelledby="actions-waiting-heading"><h2 id="actions-waiting-heading">Waiting</h2><p>Items waiting on another person or prerequisite.</p><div className="actions-list">{groupedActions.waiting.map((action) => <ActionRow key={`${action.sourceType}-${action.sourceId}`} action={action} />)}</div></section> : null}
        {groupedActions.information.length ? <section className="actions-state-group" aria-labelledby="actions-information-heading"><h2 id="actions-information-heading">For information</h2><p>Items retained for context that do not currently require your action.</p><div className="actions-list">{groupedActions.information.map((action) => <ActionRow key={`${action.sourceType}-${action.sourceId}`} action={action} />)}</div></section> : null}
      </div> : <div className="actions-list">{data.items.map((action) => <ActionRow key={`${action.sourceType}-${action.sourceId}`} action={action} />)}</div>) : null}
    </section>

    {!actionsQuery.isLoading && !actionsQuery.isError && data ? <nav className="actions-pagination" aria-label="Actions pages">
      <button className="button secondary" type="button" disabled={pageIndex === 0} onClick={() => setPageIndex((currentPage) => Math.max(0, currentPage - 1))}>Previous page</button>
      <span>Page {pageIndex + 1}</span>
      <button className="button secondary" type="button" disabled={!data.nextCursor} onClick={moveToNextPage}>Next page</button>
    </nav> : null}
  </main>
}
